import { useEffect, useRef, useState } from 'react';
import { View, Alert, Platform, ActivityIndicator, ActionSheetIOS } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { decode as base64Decode } from 'base-64';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Body, Caption, ThemedText } from '@/components/ui/Text';
import { uploadReceipt } from '@/services/receiptUpload';
import { subscribeToTripExpenses, updateExpense } from '@/services/expenses';
import type { Expense, ExpenseCategory } from '@/services/expenses';
import { ReceiptCorrectionModal } from './ReceiptCorrectionModal';
import * as Linking from 'expo-linking';

type ReceiptUploadProps = {
  tripId: string;
  userId: string;
  onReceiptProcessed?: (expense: Expense) => void;
  defaultCurrency?: string;
  noCard?: boolean; // If true, render without Card wrapper
};

export function ReceiptUpload({
  tripId,
  userId,
  onReceiptProcessed,
  defaultCurrency = 'USD',
  noCard = false,
}: ReceiptUploadProps) {
  const theme = useTheme();
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingExpenseId, setProcessingExpenseId] = useState<string | null>(null);
  const [lastUpload, setLastUpload] = useState<{
    file: Blob | File;
    fileName?: string;
    mimeType?: string;
  } | null>(null);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [processedExpense, setProcessedExpense] = useState<Expense | null>(null);

  const subscriptionRef = useRef<(() => void) | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tear down any pending receipt subscription when the component unmounts OR
  // when `tripId` changes — otherwise switching trips while an OCR is pending
  // leaks the prior trip's listener and leaves `processingExpenseId` stale.
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current();
        subscriptionRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setProcessing(false);
      setProcessingExpenseId(null);
    };
  }, [tripId]);

  const cleanupSubscriptions = () => {
    if (subscriptionRef.current) {
      subscriptionRef.current();
      subscriptionRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const startProcessingSubscription = (expenseId: string) => {
    cleanupSubscriptions();

    subscriptionRef.current = subscribeToTripExpenses(tripId, (expenses) => {
      const processedExpense = expenses.find((expense) => expense.id === expenseId);
      if (!processedExpense) return;

      if (processedExpense.status === 'parsed') {
        cleanupSubscriptions();
        setProcessing(false);
        setProcessingExpenseId(null);
        setProcessedExpense(processedExpense);
        const missingCoreFields = (!processedExpense.amount || processedExpense.amount <= 0) 
          && !processedExpense.description 
          && !processedExpense.merchant;

        // Explicitly handle blank parses so users aren’t stuck
        if (missingCoreFields) {
          Alert.alert(
            'Couldn’t read that receipt',
            'We couldn’t extract details from this file. You can try again or enter it manually.',
            [
              {
                text: 'Try again',
                onPress: () => {
                  if (lastUpload) {
                    const retryBlob = lastUpload.file.slice(
                      0,
                      lastUpload.file.size,
                      lastUpload.mimeType || lastUpload.file.type || 'application/octet-stream',
                    );
                    void handleReceiptUpload(retryBlob, {
                      fileName: lastUpload.fileName,
                      mimeType: lastUpload.mimeType,
                    });
                  } else {
                    void handlePickDocument();
                  }
                },
              },
              {
                text: 'Enter manually',
                onPress: () => {
                  if (onReceiptProcessed) {
                    onReceiptProcessed(processedExpense);
                  }
                },
              },
              { text: 'Cancel', style: 'cancel' },
            ],
          );
          return;
        }

        // Show correction modal if amount is missing or seems incorrect
        if (!processedExpense.amount || processedExpense.amount <= 0) {
          setShowCorrectionModal(true);
        } else if (onReceiptProcessed) {
          onReceiptProcessed(processedExpense);
        }
      } else if (processedExpense.status === 'failed') {
        cleanupSubscriptions();
        setProcessing(false);
        setProcessingExpenseId(null);

        Alert.alert(
          'Couldn’t read that receipt',
          'Try again with a clearer photo or enter the expense manually.',
          [
            {
              text: 'Try again',
              onPress: () => {
                if (lastUpload) {
                  const retryBlob = lastUpload.file.slice(
                    0,
                    lastUpload.file.size,
                    lastUpload.mimeType || lastUpload.file.type || 'application/octet-stream',
                  );
                  void handleReceiptUpload(retryBlob, {
                    fileName: lastUpload.fileName,
                    mimeType: lastUpload.mimeType,
                  });
                } else {
                  void handlePickDocument();
                }
              },
            },
            {
              text: 'Enter manually',
              onPress: () => {
                if (onReceiptProcessed) {
                  onReceiptProcessed(processedExpense);
                }
              },
            },
            { text: 'Cancel', style: 'cancel' },
          ],
        );
      }
    });

    timeoutRef.current = setTimeout(() => {
      cleanupSubscriptions();
      setProcessing(false);
      setProcessingExpenseId(null);
      Alert.alert(
        'OCR taking longer than expected',
        'The receipt has been saved to your expenses. You can edit it manually from the expense list, or wait for OCR to complete in the background.',
        [
          {
            text: 'Edit now',
            onPress: () => {
              // Trigger the callback to open the expense for editing
              if (onReceiptProcessed && processingExpenseId) {
                // Create a minimal expense object so the user can edit
                onReceiptProcessed({
                  id: processingExpenseId,
                  tripId,
                  amount: 0,
                  currency: defaultCurrency,
                  description: 'Scanned receipt',
                  category: 'other' as const,
                  date: new Date(),
                  status: 'pending',
                  createdBy: userId,
                  createdAt: new Date(),
                  updatedAt: new Date(),
                });
              }
            },
          },
          { text: 'Wait', style: 'cancel' },
        ],
      );
    }, 180000);
  };

  const handleReceiptUpload = async (
    file: Blob | File,
    options: { fileName?: string; mimeType?: string },
    opts?: { preSetUploading?: boolean },
  ) => {
    try {
      if (!opts?.preSetUploading) {
        setUploading(true);
      }
      setProcessingExpenseId(null);

      const clonedFile = file.slice(0, file.size, options.mimeType || (file as File).type);
      setLastUpload({ file: clonedFile, fileName: options.fileName, mimeType: options.mimeType });

      console.log('[ReceiptUpload] Upload start', {
        size: file.size,
        fileName: options.fileName,
        mimeType: options.mimeType || (file as File).type,
      });

      const uploadResult = await uploadReceipt(tripId, userId, file, {
        currency: defaultCurrency,
        fileName: options.fileName,
        mimeType: options.mimeType,
      });

      console.log('[ReceiptUpload] Upload result', uploadResult);

      if (!uploadResult.success || !uploadResult.expenseId) {
        setUploading(false);
        setProcessing(false);
        setProcessingExpenseId(null);
        Alert.alert('Upload Failed', uploadResult.error || 'Could not upload receipt');
        return;
      }

      setProcessing(true);
      setProcessingExpenseId(uploadResult.expenseId);

      Alert.alert(
        'Receipt uploaded',
        'We\'re extracting the details now. You\'ll see it in your expense list with an "OCR" badge while processing. You can edit it anytime.',
        [{ text: 'OK' }],
      );

      startProcessingSubscription(uploadResult.expenseId);
    } catch (error) {
      console.error('Error uploading receipt:', error);
      Alert.alert('Error', 'Failed to upload receipt. Please try again.');
      setProcessing(false);
      setProcessingExpenseId(null);
    } finally {
      setUploading(false);
    }
  };

  const convertUriToBlob = async (uri: string, fallbackMimeType: string) => {
    try {
      const response = await fetch(uri);
      if (!response.ok) {
        throw new Error('Unable to read file');
      }
      const blob = await response.blob();
      if (!blob.type && fallbackMimeType) {
        return blob.slice(0, blob.size, fallbackMimeType);
      }
      return blob;
    } catch (fetchError) {
      try {
        const FileSystem = await import('@/lib/fileSystemLegacy');
        const base64 = await FileSystem.readAsStringAsync(uri, {
          encoding: FileSystem.EncodingType.Base64,
        });
        const byteCharacters = base64Decode(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: fallbackMimeType });
      } catch (base64Error) {
        console.error('Failed to read file as blob:', fetchError, base64Error);
        throw fetchError;
      }
    }
  };

  const handlePickImage = async () => {
    try {
      // Request permissions
      if (Platform.OS !== 'web') {
        const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (status !== 'granted') {
          Alert.alert(
            'Permission Required',
            'We need photo library access to upload receipts. You can still use Take Photo or Upload PDF.',
            [
              { text: 'Not now', style: 'cancel' },
              { text: 'Open Settings', onPress: () => void Linking.openSettings() },
            ],
          );
          return;
        }
      }

      // Launch image picker
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        // Don't force cropping; users often need the full receipt.
        allowsEditing: false,
        quality: 0.8,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      const asset = result.assets[0];

      try {
        // Show working state before potentially expensive file conversion.
        setUploading(true);
        await new Promise((resolve) => setTimeout(resolve, 0));

        let sourceUri = asset.uri;
        const isHeic =
          asset.mimeType?.toLowerCase().includes('heic') ||
          asset.mimeType?.toLowerCase().includes('heif') ||
          asset.fileName?.toLowerCase().endsWith('.heic') ||
          asset.fileName?.toLowerCase().endsWith('.heif');

        if (asset.type?.toLowerCase() === 'image' && isHeic) {
          const manipulated = await ImageManipulator.manipulateAsync(
            asset.uri,
            [],
            { compress: 0.9, format: ImageManipulator.SaveFormat.JPEG },
          );
          sourceUri = manipulated.uri;
        }

        const blob = await convertUriToBlob(sourceUri, 'image/jpeg');
        await handleReceiptUpload(blob, {
          fileName: asset.fileName || 'receipt.jpg',
          mimeType: blob.type || 'image/jpeg',
        }, { preSetUploading: true });
      } catch (error) {
        console.error('Error uploading receipt:', error);
        setUploading(false);
        Alert.alert('Error', 'Failed to upload receipt. Please try again.');
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to access photo library. Please try again.');
    }
  };

  const handleTakePhoto = async () => {
    try {
      // Request camera permissions
      if (Platform.OS !== 'web') {
        const { status } = await ImagePicker.requestCameraPermissionsAsync();
        if (status !== 'granted') {
          Alert.alert(
            'Permission Required',
            'We need camera access to take receipt photos. You can still upload from library or PDF.',
            [
              { text: 'Not now', style: 'cancel' },
              { text: 'Open Settings', onPress: () => void Linking.openSettings() },
            ],
          );
          return;
        }
      }

      // Launch camera
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        // Don't force cropping; users often need the full receipt.
        allowsEditing: false,
        quality: 0.8,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      const asset = result.assets[0];

      try {
        // Show working state before potentially expensive file conversion.
        setUploading(true);
        await new Promise((resolve) => setTimeout(resolve, 0));

        let sourceUri = asset.uri;
        const isHeic =
          asset.mimeType?.toLowerCase().includes('heic') ||
          asset.mimeType?.toLowerCase().includes('heif') ||
          asset.fileName?.toLowerCase().endsWith('.heic') ||
          asset.fileName?.toLowerCase().endsWith('.heif');

        if (asset.type?.toLowerCase() === 'image' && isHeic) {
          const manipulated = await ImageManipulator.manipulateAsync(
            asset.uri,
            [],
            { compress: 0.9, format: ImageManipulator.SaveFormat.JPEG },
          );
          sourceUri = manipulated.uri;
        }

        const blob = await convertUriToBlob(sourceUri, 'image/jpeg');
        await handleReceiptUpload(blob, {
          fileName: asset.fileName || `receipt_${Date.now()}.jpg`,
          mimeType: blob.type || 'image/jpeg',
        }, { preSetUploading: true });
      } catch (error) {
        console.error('Error uploading receipt:', error);
        setUploading(false);
        Alert.alert('Error', 'Failed to upload receipt. Please try again.');
      }
    } catch (error) {
      console.error('Error taking photo:', error);
      Alert.alert('Error', 'Failed to access camera. Please try again.');
    }
  };

  const handlePickDocument = async (allowMultiple = false) => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/jpeg', 'image/png', 'application/pdf'],
        copyToCacheDirectory: true,
        multiple: allowMultiple,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      const asset = result.assets[0];

      if (asset.size && asset.size > 8 * 1024 * 1024) {
        Alert.alert('File too large', 'Receipts must be smaller than 8MB.');
        return;
      }

      let sourceUri = asset.uri;
      let mimeType = asset.mimeType || 'application/pdf';
      const fileNameLower = (asset.name || '').toLowerCase();
      const isHeic =
        mimeType.toLowerCase().includes('heic') ||
        mimeType.toLowerCase().includes('heif') ||
        fileNameLower.endsWith('.heic') ||
        fileNameLower.endsWith('.heif');

      if (isHeic) {
        const manipulated = await ImageManipulator.manipulateAsync(
          asset.uri,
          [],
          { compress: 0.9, format: ImageManipulator.SaveFormat.JPEG },
        );
        sourceUri = manipulated.uri;
        mimeType = 'image/jpeg';
      }

      // Show working state before potentially expensive file conversion.
      setUploading(true);
      await new Promise((resolve) => setTimeout(resolve, 0));

      const blob = await convertUriToBlob(sourceUri, mimeType);
      await handleReceiptUpload(blob, {
        fileName: asset.name || 'receipt',
        mimeType: mimeType || blob.type || 'application/pdf',
      }, { preSetUploading: true });
    } catch (error) {
      console.error('Error picking document:', error);
      setUploading(false);
      Alert.alert('Error', 'Failed to open documents. Please try again.');
    }
  };

  const handleUploadPress = () => {
    if (uploading || processing) {
      return;
    }

    if (Platform.OS === 'ios') {
      ActionSheetIOS.showActionSheetWithOptions(
        {
          options: ['Take Photo', 'Photo Library', 'Browse Files', 'Cancel'],
          cancelButtonIndex: 3,
        },
        (buttonIndex) => {
          if (buttonIndex === 0) {
            void handleTakePhoto();
          } else if (buttonIndex === 1) {
            void handlePickImage();
          } else if (buttonIndex === 2) {
            void handlePickDocument();
          }
        },
      );
    } else if (Platform.OS === 'android') {
      Alert.alert(
        'Upload receipt',
        'Choose where to upload your receipt from.',
        [
          { text: 'Take photo', onPress: () => void handleTakePhoto() },
          { text: 'Photo library', onPress: () => void handlePickImage() },
          { text: 'Browse files', onPress: () => void handlePickDocument() },
          { text: 'Cancel', style: 'cancel' },
        ],
      );
    } else {
      void handlePickDocument();
    }
  };

  const content = (
    <View style={{ gap: theme.spacing(1.5) }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
          <Ionicons name="camera-outline" size={20} color={theme.colors.primary} />
          <ThemedText variant="bodyStrong">Scan receipt</ThemedText>
        </View>
        <View style={{
          paddingHorizontal: theme.spacing(1),
          paddingVertical: theme.spacing(0.5),
          borderRadius: theme.radii.pill,
          backgroundColor: (processing || uploading) ? theme.colors.warning + '20' : theme.colors.success + '20',
        }}>
          <Caption color={(processing || uploading) ? theme.colors.warning : theme.colors.success} style={{ fontSize: 11, fontWeight: '700' }}>
            {(processing || uploading) ? 'Working' : 'Ready'}
          </Caption>
        </View>
      </View>
      <View style={{ gap: theme.spacing(0.5) }}>
        <Caption color={theme.colors.textSecondary}>
          Auto-extract amount, merchant, and date from a photo or PDF.
        </Caption>
        <Caption color={theme.colors.textSecondary}>
          Supports JPG, PNG, or PDF up to 8MB.
        </Caption>
      </View>
      
      {processing && (
        <View style={{ 
          flexDirection: 'row', 
          alignItems: 'center', 
          gap: theme.spacing(1), 
          paddingVertical: theme.spacing(1),
          borderRadius: theme.radii.md,
          backgroundColor: theme.colors.borderMuted + '25',
        }}>
          <ActivityIndicator size="small" color={theme.colors.primary} />
          <Caption color={theme.colors.textSecondary}>
            Processing with OCR{processingExpenseId ? ` (#${processingExpenseId.slice(0, 6)})` : ''}…
          </Caption>
        </View>
      )}

      <Button
        label={uploading ? 'Uploading…' : processing ? 'Processing…' : 'Upload receipt'}
        variant="primary"
        onPress={handleUploadPress}
        disabled={uploading || processing}
        icon={
          uploading || processing
            ? undefined
            : <Ionicons name="document-text-outline" size={18} color={theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF'} />
        }
      />
    </View>
  );

  const handleSaveCorrections = async (corrections: {
    amount?: number;
    currency?: string;
    merchant?: string;
    description?: string;
    date?: Date;
    category?: ExpenseCategory;
  }) => {
    if (!processedExpense) return;

    try {
      await updateExpense(tripId, processedExpense.id, corrections);
      setShowCorrectionModal(false);
      if (onReceiptProcessed) {
        onReceiptProcessed({ ...processedExpense, ...corrections });
      }
    } catch (error) {
      console.error('[ReceiptUpload] Error saving corrections:', error);
      Alert.alert('Error', 'Failed to save corrections. Please try again.');
    }
  };

  if (noCard) {
    return (
      <>
        {content}
        <ReceiptCorrectionModal
          visible={showCorrectionModal}
          expense={processedExpense}
          onClose={() => {
            setShowCorrectionModal(false);
            if (processedExpense && onReceiptProcessed) {
              onReceiptProcessed(processedExpense);
            }
          }}
          onSave={handleSaveCorrections}
        />
      </>
    );
  }

  return (
    <>
      <Card variant="outlined">
        {content}
      </Card>
      <ReceiptCorrectionModal
        visible={showCorrectionModal}
        expense={processedExpense}
        onClose={() => {
          setShowCorrectionModal(false);
          if (processedExpense && onReceiptProcessed) {
            onReceiptProcessed(processedExpense);
          }
        }}
        onSave={handleSaveCorrections}
      />
    </>
  );
}

